import apiClient from "../../services/apiClient";
import { useEffect, useState, useContext, useMemo, useRef } from "react";
import {
  Input,
  Select,
  Button,
  Card,
  Table,
  Modal,
  Tag,
  Tooltip,
  Pagination,
  Spin,
  Alert,
  Form,
  message,
  Popconfirm,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  RiseOutlined,
  DollarOutlined,
  FileTextOutlined,
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownloadOutlined,
  BankOutlined,
  AppstoreOutlined,
  TableOutlined,
  PercentageOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./NSDL.css";
import { useNavigate } from "react-router-dom";

const { Search } = Input;
const { Option } = Select;

// Utility function to break camelCase keys into readable format
const camelCaseToReadable = (str) => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (match) => match.toUpperCase());
};

const Sec = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [securitisedInstruments, setSecuritisedInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("companyName");
  const [viewMode, setViewMode] = useState("table");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const editForm = useRef();
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Fetch paginated data from backend
  const fetchSecData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/nsdl/securitised-instruments?page=${page - 1}&size=${pageSize}`,
        { validateStatus: () => true }
      );
      if (response.status === 204) {
        setSecuritisedInstruments([]);
        setPagination({ current: 1, pageSize, total: 0, totalPages: 0 });
        return;
      }
      if (response.data && (response.data.content || response.data.data)) {
        const data = response.data.content ? response.data : response.data.data;
        setSecuritisedInstruments(Array.isArray(data.content) ? data.content : []);
        setPagination({
          current: (data.pageNumber || 0) + 1,
          pageSize: data.pageSize || pageSize,
          total: data.totalElements || 0,
          totalPages: data.totalPages || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch data");
      }
    } catch (error) {
      setError("Failed to load securitised instruments data. Please try again.");
      setSecuritisedInstruments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecData(pagination.current, pagination.pageSize);
    // eslint-disable-next-line
  }, []);

  // Filtering and sorting (client-side for current page)
  const filteredData = useMemo(() => {
    let filtered = Array.isArray(securitisedInstruments) ? securitisedInstruments : [];
    if (!Array.isArray(filtered)) return [];
    if (searchText) {
      filtered = filtered.filter((item) => {
        const searchLower = searchText.toLowerCase();
        return (
          (item.companyName && item.companyName.toLowerCase().includes(searchLower)) ||
          (item.descriptionInNsdl && item.descriptionInNsdl.toLowerCase().includes(searchLower)) ||
          (item.isin && item.isin.toLowerCase().includes(searchLower))
        );
      });
    }
    if (selectedCompany !== "all") {
      filtered = filtered.filter((item) => item.companyName === selectedCompany);
    }
    if (selectedType !== "all") {
      filtered = filtered.filter((item) => item.nameOfTheInstrument?.includes(selectedType));
    }
    if (selectedStatus !== "all") {
      filtered = filtered.filter((item) => item.listingStatus === selectedStatus);
    }
    if (Array.isArray(filtered)) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        if (aValue === undefined) aValue = '';
        if (bValue === undefined) bValue = '';
        if (typeof aValue === "number" && typeof bValue === "number") {
          return bValue - aValue;
        }
        return String(aValue).localeCompare(String(bValue));
      });
    }
    return filtered;
  }, [securitisedInstruments, searchText, selectedCompany, selectedType, selectedStatus, sortBy]);

  // Unique values for filters (from current page)
  const { companies, types, statuses } = useMemo(() => {
    const uniqueCompanies = [
      ...new Set(securitisedInstruments.map((item) => item.companyName)),
    ]
      .filter(Boolean)
      .slice(0, 50);
    const uniqueTypes = [
      ...new Set(securitisedInstruments.map((item) => item.nameOfTheInstrument)),
    ].filter(Boolean);
    const uniqueStatuses = [
      ...new Set(securitisedInstruments.map((item) => item.listingStatus)),
    ].filter(Boolean);
    return {
      companies: uniqueCompanies,
      types: uniqueTypes,
      statuses: uniqueStatuses,
    };
  }, [securitisedInstruments]);

  // Pagination change handler
  const handlePageChange = (page, pageSize) => {
    fetchSecData(page, pageSize);
  };

  // Memoized stats
  const stats = useMemo(
    () => [
      {
        title: "Total Instruments",
        value: pagination.total.toLocaleString(),
        icon: <FileTextOutlined />,
        color: "#4ecdc4",
      },
      {
        title: "Active Issues",
        value: securitisedInstruments
          .filter(
            (item) => item.listingStatus === "Active"
          )
          .length.toLocaleString(),
        icon: <RiseOutlined />,
        color: "#667eea",
      },
      {
        title: "Total Issue Size",
        value:
          "₹" +
          (
            securitisedInstruments.reduce(
              (sum, item) => sum + (parseFloat(item.faceValue) || 0),
              0
            ) / 10000000
          ).toFixed(2) +
          " Cr",
        icon: <DollarOutlined />,
        color: "#ff6b6b",
      },
      {
        title: "Avg Coupon Rate",
        value:
          (
            securitisedInstruments.reduce((sum, item) => {
              const rate = parseFloat(item.couponRate?.replace("%", "")) || 0;
              return sum + rate;
            }, 0) /
            securitisedInstruments.filter((item) => item.couponRate).length
          ).toFixed(1) + "%",
        icon: <PercentageOutlined />,
        color: "#feca57",
      },
    ],
    [securitisedInstruments, pagination.total]
  );

  const handleRefresh = () => {
    fetchSecData();
  };

  const handleExport = () => {
    // Export functionality
    console.log("Export functionality to be implemented");
  };

  const showRecordDetails = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const getStatusColor = (redemptionDate) => {
    if (!redemptionDate) return "default";
    const redemption = new Date(redemptionDate);
    const today = new Date();
    return redemption > today ? "green" : "red";
  };

  const getTypeColor = (instrumentName) => {
    if (instrumentName?.includes("PTC")) return "blue";
    if (instrumentName?.includes("SR")) return "purple";
    return "default";
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined || value === "") return "N/A";

    if (key === "issueDate" || key === "redemption") {
      return value;
    }

    if (key === "issuePrice" || key === "faceValue") {
      return `₹${value}`;
    }

    if (key === "couponRate") {
      return value || "N/A";
    }

    return value.toString();
  };

  const handleEdit = (record) => {
    setEditData(record);
    setEditModalVisible(true);
  };
  const handleUpdate = async (values) => {
    setEditLoading(true);
    try {
      await apiClient.put(`${baseUrl}/nsdl/securitised-instruments/${editData.isin}`, values);
      message.success('Record updated successfully');
      setEditModalVisible(false);
      fetchSecData();
    } catch (error) {
      message.error('Failed to update record');
    } finally {
      setEditLoading(false);
    }
  };
  const handleDelete = async (isin) => {
    try {
      await apiClient.delete(`${baseUrl}/nsdl/securitised-instruments/${isin}`);
      message.success('Record deleted successfully');
      fetchSecData();
    } catch (error) {
      message.error('Failed to delete record');
    }
  };

  return (
    <div className={`nsdl-container ${isDark ? "dark" : ""}`}>
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/d/nsdl")}
        style={{ marginBottom: 16 }}
      >
        Back to NSDL
      </Button>
      {/* Hero Section */}
      <div
        className="nsdl-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              borderRadius: 16,
            }}
          />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                }}
              >
                Securitised Instruments
              </h1>
              <p
                style={{
                  fontSize: 20,
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                Comprehensive database of securitised instruments including PTCs, Security Receipts, and asset-backed securities
              </p>
            </div>

            {/* Stats on the right side - 2 per row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
              {/* First row - 2 stats */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px'
                  }}>
                    <div
                      style={{
                        color: stat.color,
                        fontSize: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: isDark ? "#ffffff" : "#1a1a1a",
                        lineHeight: 1
                      }}>
                        {stat.value}
                      </span>
                      <span style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                        fontWeight: 500
                      }}>
                        {stat.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Second row - remaining stats */}
              {stats.length > 2 && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  {stats.slice(2).map((stat, index) => (
                    <div key={index + 2} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                      padding: '12px 16px',
                      borderRadius: 12,
                      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      minWidth: '180px'
                    }}>
                      <div
                        style={{
                          color: stat.color,
                          fontSize: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 36,
                          height: 36,
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 8,
                          boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: isDark ? "#ffffff" : "#1a1a1a",
                          lineHeight: 1
                        }}>
                          {stat.value}
                        </span>
                        <span style={{
                          fontSize: 12,
                          color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                          fontWeight: 500
                        }}>
                          {stat.title}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="nsdl-section">
        <div className="nsdl-controls">
          <div className="nsdl-controls-left">
            <Search
              placeholder="Search by ISIN, company, or instrument name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
            <Select
              value={selectedCompany}
              onChange={setSelectedCompany}
              placeholder="Filter by Company"
              style={{ width: 200 }}
              allowClear
            >
              <Option value="all">All Companies</Option>
              {companies.map((company) => (
                <Option key={company} value={company}>
                  {company}
                </Option>
              ))}
            </Select>
            <Select
              value={selectedType}
              onChange={setSelectedType}
              placeholder="Filter by Type"
              style={{ width: 150 }}
              allowClear
            >
              <Option value="all">All Types</Option>
              {types.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Filter by Status"
              style={{ width: 120 }}
              allowClear
            >
              <Option value="all">All Status</Option>
              {statuses.map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </div>

          <div className="nsdl-controls-right">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export
            </Button>
            <div className="view-controls">
              <Button
                type={viewMode === "cards" ? "primary" : "default"}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode("cards")}
              />
              <Button
                type={viewMode === "table" ? "primary" : "default"}
                icon={<TableOutlined />}
                onClick={() => setViewMode("table")}
              />
            </div>
          </div>
        </div>

        <div className="nsdl-results-info">
          <span>
            Showing {filteredData.length} of {pagination.total} instruments (Page {pagination.current} of {pagination.totalPages})
          </span>
        </div>

        {/* Results Section */}
        <div className="results-section">
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              closable
              style={{ marginBottom: "1rem" }}
            />
          )}

          {loading ? (
            <div className="nsdl-loading">
              <Spin size="large" />
              <p>Loading securitised instruments data...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="nsdl-empty">
              <FileTextOutlined />
              <p>No securitised instruments found matching your criteria</p>
            </div>
          ) : viewMode === "cards" ? (
            <>
              <div className="nsdl-cards-grid">
                {filteredData.map((record, index) => (
                  <Card
                    key={index}
                    className="nsdl-data-card"
                    hoverable
                  >
                    <div className="nsdl-card-header">
                      <h3 className="nsdl-card-title">
                        <span className="nsdl-isin-code">
                          {record.isin || `ISIN ${index + 1}`}
                        </span>
                      </h3>
                      <Tag color={getStatusColor(record.redemption)}>
                        {record.redemption &&
                          new Date(record.redemption) > new Date()
                          ? "Active"
                          : "Matured"}
                      </Tag>
                    </div>

                    <div className="nsdl-card-content">
                      <div className="nsdl-card-field">
                        <span className="nsdl-field-label">Company:</span>
                        <Tooltip title={record.companyName || "N/A"}>
                          <span className="nsdl-field-value nsdl-company-name">
                            {record.companyName || "N/A"}
                          </span>
                        </Tooltip>
                      </div>

                      <div className="nsdl-card-field">
                        <span className="nsdl-field-label">
                          Instrument Name:
                        </span>
                        <span className="nsdl-field-value">
                          {record.nameOfTheInstrument || "N/A"}
                        </span>
                      </div>

                      <div className="nsdl-card-field">
                        <span className="nsdl-field-label">Issue Price:</span>
                        <span className="nsdl-field-value">
                          {formatValue(record.issuePrice, "issuePrice")}
                        </span>
                      </div>

                      <div className="nsdl-card-field">
                        <span className="nsdl-field-label">Face Value:</span>
                        <span className="nsdl-field-value">
                          {formatValue(record.faceValue, "faceValue")}
                        </span>
                      </div>

                      <div className="nsdl-card-field">
                        <span className="nsdl-field-label">Coupon Rate:</span>
                        <span className="nsdl-field-value">
                          {formatValue(record.couponRate, "couponRate")}
                        </span>
                      </div>

                      <div className="nsdl-card-field">
                        <span className="nsdl-field-label">Issue Date:</span>
                        <span className="nsdl-field-value">
                          {formatValue(record.issueDate, "issueDate")}
                        </span>
                      </div>

                      <div className="nsdl-card-field">
                        <span className="nsdl-field-label">
                          Redemption Date:
                        </span>
                        <span className="nsdl-field-value">
                          {formatValue(record.redemption, "redemption")}
                        </span>
                      </div>
                    </div>
                    <div className="card-actions" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
                      <Tooltip title="View Details">
                        <Button
                          type="primary"
                          icon={<EyeOutlined />}
                          onClick={() => showRecordDetails(record)}
                        />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(record)}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Popconfirm
                          title="Are you sure you want to delete this record?"
                          onConfirm={() => handleDelete(record.isin)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Tooltip>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <div style={{ textAlign: "center", marginTop: "2rem" }}>
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  onChange={handlePageChange}
                  className="custom-pagination"
                />
              </div>
            </>
          ) : (
            <>
              <Table
                dataSource={filteredData}
                columns={[
                  {
                    title: "ISIN",
                    dataIndex: "isin",
                    key: "isin",
                    width: 150,
                    render: (text) => (
                      <div className="nsdl-table-cell">
                        <div className="nsdl-table-cell-title">
                          {text || "N/A"}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: "Company",
                    dataIndex: "companyName",
                    key: "companyName",
                    render: (text) => (
                      <div className="nsdl-table-cell">
                        <div className="nsdl-table-cell-title">
                          {text || "N/A"}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: "Instrument Name",
                    dataIndex: "nameOfTheInstrument",
                    key: "nameOfTheInstrument",
                    render: (text) => (
                      <div className="nsdl-table-cell">
                        <div className="nsdl-table-cell-title">
                          {text || "N/A"}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: "Issue Price",
                    dataIndex: "issuePrice",
                    key: "issuePrice",
                    render: (value) => formatValue(value, "issuePrice"),
                  },
                  {
                    title: "Face Value",
                    dataIndex: "faceValue",
                    key: "faceValue",
                    render: (value) => formatValue(value, "faceValue"),
                  },
                  {
                    title: "Coupon Rate",
                    dataIndex: "couponRate",
                    key: "couponRate",
                    render: (value) => formatValue(value, "couponRate"),
                  },
                  {
                    title: "Status",
                    key: "status",
                    render: (_, record) => (
                      <Tag color={getStatusColor(record.redemption)}>
                        {record.redemption &&
                          new Date(record.redemption) > new Date()
                          ? "Active"
                          : "Matured"}
                      </Tag>
                    ),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 150,
                    render: (_, record) => (
                      <div className="nsdl-table-actions">
                        <Tooltip title="View">
                          <Button type="link" icon={<EyeOutlined />} onClick={() => showRecordDetails(record)} />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                        </Tooltip>
                        <Popconfirm
                          title="Are you sure you want to delete this record?"
                          onConfirm={() => handleDelete(record.isin)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Tooltip title="Delete">
                            <Button type="link" icon={<DeleteOutlined />} danger />
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    ),
                  },
                ]}
                rowKey={(record, index) => index}
                pagination={false}
                className={`nsdl-table ${isDark ? "dark" : ""}`}
              />

              {/* Pagination */}
              <div style={{ textAlign: "center", marginTop: "2rem" }}>
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  onChange={handlePageChange}
                  className="custom-pagination"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <FileTextOutlined
              style={{ fontSize: "1.5rem", color: "#667eea" }}
            />
            <span>Securitised Instrument Details</span>
          </div>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={900}
        className={`nsdl-detail-modal ${isDark ? "dark" : ""}`}
        bodyStyle={{
          backgroundColor: isDark ? "#2a2a3e" : "#ffffff",
          color: isDark ? "#e0e0e0" : "#333333",
          maxHeight: "70vh",
          overflowY: "auto",
          padding: "0",
        }}
      >
        {selectedRecord && (
          <div className="nsdl-detail-content" style={{ padding: "1.5rem" }}>
            {/* Header Section */}
            <div
              style={{
                background: isDark ? "#3a3a4e" : "#f8f9fa",
                padding: "1.5rem",
                borderRadius: "12px",
                marginBottom: "2rem",
                border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "1rem",
                }}
              >
                <div>
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      display: "inline-block",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {selectedRecord.isin || "N/A"}
                  </div>
                  <h3
                    style={{
                      margin: "0.5rem 0",
                      fontSize: "1.3rem",
                      fontWeight: "600",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {selectedRecord.companyName || "N/A"}
                  </h3>
                  <p
                    style={{
                      margin: "0",
                      fontSize: "0.9rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      lineHeight: "1.4",
                    }}
                  >
                    {selectedRecord.nameOfTheInstrument || "N/A"}
                  </p>
                </div>
                <Tag
                  color={getStatusColor(selectedRecord.redemption)}
                  style={{ fontSize: "0.9rem", padding: "0.5rem 1rem" }}
                >
                  {selectedRecord.redemption &&
                    new Date(selectedRecord.redemption) > new Date()
                    ? "Active"
                    : "Matured"}
                </Tag>
              </div>

              <div
                style={{
                  background: isDark ? "#2a2a3e" : "#ffffff",
                  padding: "1rem",
                  borderRadius: "8px",
                  border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                }}
              >
                <p
                  style={{
                    margin: "0",
                    fontSize: "0.9rem",
                    color: isDark ? "#c0c0c0" : "#555555",
                    fontStyle: "italic",
                  }}
                >
                  {selectedRecord.descriptionInNsdl || "N/A"}
                </p>
              </div>
            </div>

            {/* Financial Details Section */}
            <div style={{ marginBottom: "2rem" }}>
              <h4
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: isDark ? "#e0e0e0" : "#333333",
                  borderBottom: isDark
                    ? "1px solid #4a4a5e"
                    : "1px solid #e9ecef",
                  paddingBottom: "0.5rem",
                }}
              >
                <DollarOutlined
                  style={{ marginRight: "0.5rem", color: "#667eea" }}
                />
                Financial Details
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Issue Price
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {formatValue(selectedRecord.issuePrice, "issuePrice")}
                  </div>
                </div>
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Face Value
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {formatValue(selectedRecord.faceValue, "faceValue")}
                  </div>
                </div>
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Coupon Rate
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {formatValue(selectedRecord.couponRate, "couponRate")}
                  </div>
                </div>
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Certificate Numbers
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {selectedRecord.certificateNos || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Section */}
            <div style={{ marginBottom: "2rem" }}>
              <h4
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: isDark ? "#e0e0e0" : "#333333",
                  borderBottom: isDark
                    ? "1px solid #4a4a5e"
                    : "1px solid #e9ecef",
                  paddingBottom: "0.5rem",
                }}
              >
                <CalendarOutlined
                  style={{ marginRight: "0.5rem", color: "#667eea" }}
                />
                Timeline
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Issue Date
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {formatValue(selectedRecord.issueDate, "issueDate")}
                  </div>
                </div>
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Redemption Date
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {formatValue(selectedRecord.redemption, "redemption")}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details Section */}
            <div style={{ marginBottom: "2rem" }}>
              <h4
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: isDark ? "#e0e0e0" : "#333333",
                  borderBottom: isDark
                    ? "1px solid #4a4a5e"
                    : "1px solid #e9ecef",
                  paddingBottom: "0.5rem",
                }}
              >
                <FileTextOutlined
                  style={{ marginRight: "0.5rem", color: "#667eea" }}
                />
                Additional Details
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Frequency of Interest Payment
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {selectedRecord.frequencyOfInterestPayment || "N/A"}
                  </div>
                </div>
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Put/Call Option
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {selectedRecord.putCallOption || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div>
              <h4
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: isDark ? "#e0e0e0" : "#333333",
                  borderBottom: isDark
                    ? "1px solid #4a4a5e"
                    : "1px solid #e9ecef",
                  paddingBottom: "0.5rem",
                }}
              >
                <BankOutlined
                  style={{ marginRight: "0.5rem", color: "#667eea" }}
                />
                Contact Information
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Registrar with BP ID
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: isDark ? "#e0e0e0" : "#333333",
                    }}
                  >
                    {selectedRecord.registrarWithBpIdNo || "N/A"}
                  </div>
                </div>
                <div
                  style={{
                    background: isDark ? "#3a3a4e" : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: isDark ? "1px solid #4a4a5e" : "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: isDark ? "#b0b0b0" : "#666666",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Contact Person
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: isDark ? "#e0e0e0" : "#333333",
                      lineHeight: "1.4",
                    }}
                  >
                    {selectedRecord.contactPersonOfTheCompany || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      {editModalVisible && (
        <Modal
          title="Edit Record"
          visible={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          footer={null}
        >
          <Form
            initialValues={editData}
            onFinish={handleUpdate}
            layout="vertical"
            ref={editForm}
          >
            <Form.Item noStyle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                {(() => {
                  const fields = Object.entries(editData).filter(([key]) => key !== 'createdAt' && key !== 'updatedAt');
                  const items = [];
                  for (let i = 0; i < fields.length; i += 2) {
                    const [key1] = fields[i];
                    const [key2] = fields[i + 1] || [];
                    items.push(
                      <div key={key1 + (key2 || '')} style={{ display: 'flex', width: '100%', gap: 16 }}>
                        <Form.Item
                          key={key1}
                          label={camelCaseToReadable(key1)}
                          name={key1}
                          rules={key1 === "isin" ? [] : [{ required: false }]}
                          style={{ flex: 1 }}
                        >
                          {key1 === "isin" ? <Input disabled /> : <Input />}
                        </Form.Item>
                        {key2 && (
                          <Form.Item
                            key={key2}
                            label={camelCaseToReadable(key2)}
                            name={key2}
                            rules={key2 === "isin" ? [] : [{ required: false }]}
                            style={{ flex: 1 }}
                          >
                            {key2 === "isin" ? <Input disabled /> : <Input />}
                          </Form.Item>
                        )}
                      </div>
                    );
                  }
                  return items;
                })()}
              </div>
            </Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button onClick={() => setEditModalVisible(false)} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Update
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default Sec;
